import { useEffect, useRef } from "react";

/**
 * Persistent system notification that reflects the current ticket state in real time.
 * Uses the Notification API with a stable tag so the same notif is REPLACED on each update
 * (no spam, no sound on updates, only visible in the system tray).
 *
 * - Creates/updates when ticket.status === "waiting" or "called"
 * - Silently updates the body on every poll (position / ETA)
 * - Cleared when ticket disappears (served, cancelled, etc.)
 *
 * Requires Notification permission. Does NOT require a push server.
 * Works while the tab/PWA is alive (foreground or backgrounded).
 */
const TAG = "bpg-ticket-active";

export function usePersistentTicketNotification(ticket, enabled) {
  const notifRef = useRef(null);
  const prevStatusRef = useRef(null);

  // Ask permission once, proactively (only if enabled and not already granted/denied)
  useEffect(() => {
    if (!enabled) return;
    if (!("Notification" in window)) return;
    if (Notification.permission === "default") {
      try { Notification.requestPermission(); } catch {}
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (!("Notification" in window) || Notification.permission !== "granted") return;

    if (!ticket || (ticket.status !== "waiting" && ticket.status !== "called")) {
      // Close the persistent notif if any
      try { notifRef.current && notifRef.current.close(); } catch {}
      notifRef.current = null;
      prevStatusRef.current = null;
      return;
    }

    const isCalled = ticket.status === "called";
    const title = isCalled
      ? `C'est à vous ! Ticket n°${ticket.number}`
      : `Ticket n°${ticket.number} · ${ticket.commerceName || ""}`;
    const body = isCalled
      ? `Présentez-vous au comptoir — ${ticket.commerceName || ""}`
      : ticket.position === 0
        ? "Vous êtes le prochain."
        : `${ticket.position} visiteur${ticket.position > 1 ? "s" : ""} avant vous · ~${ticket.estimatedWait} min`;

    // Only make NOISE (sound + vibrate via system) when the status flips to called the first time
    const silent = !(prevStatusRef.current === "waiting" && isCalled);

    try {
      // close previous instance to force refresh on some Android browsers
      if (notifRef.current) {
        try { notifRef.current.close(); } catch {}
      }
      const n = new Notification(title, {
        body,
        tag: TAG,
        renotify: !silent,
        silent,
        requireInteraction: true, // stay visible until user dismisses
        icon: "/favicon.ico",
        badge: "/favicon.ico",
        data: { ticketId: ticket.id },
      });
      notifRef.current = n;
      prevStatusRef.current = ticket.status;
    } catch {}

    return () => {
      // don't close on every render — only close when ticket disappears (handled above)
    };
  }, [enabled, ticket?.id, ticket?.status, ticket?.number, ticket?.position, ticket?.estimatedWait, ticket?.commerceName]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try { notifRef.current && notifRef.current.close(); } catch {}
      notifRef.current = null;
    };
  }, []);
}
